import React, { useState, useEffect } from 'react';
import { superAdminAPI } from '../services/api';
import Icon from './Icon';


function SuperAdminServices({ companies = [], darkMode = true }) {
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRowExpand = (serviceId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(serviceId)) {
        newSet.delete(serviceId);
      } else {
        newSet.add(serviceId);
      }
      return newSet;
    });
  };

  useEffect(() => {
    fetchServices();
  }, [selectedCompanyId, fromDate, toDate]);

  useEffect(() => {
    filterServices();
  }, [services, searchTerm]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Debug info
      console.log('=== FETCHING SERVICES ===');
      console.log('API endpoint: /api/admin/super/services');
      console.log('Selected company ID:', selectedCompanyId);
      console.log('From date:', fromDate);
      console.log('To date:', toDate);
      
      const params = {};
      if (selectedCompanyId) {
        params.company_id = selectedCompanyId;
      }
      if (fromDate) {
        params.from_date = fromDate;
      }
      if (toDate) {
        params.to_date = toDate;
      }
      
      console.log('Request params:', params);
      
      const response = await superAdminAPI.getAllServices(params);
      
      console.log('=== API RESPONSE ===');
      console.log('Status:', response.status);
      console.log('Response data:', response.data);
      console.log('Response data type:', typeof response.data);
      console.log('Services array:', response.data?.services);
      console.log('Services array type:', typeof response.data?.services);
      console.log('Services array length:', response.data?.services?.length);
      
      // Handle different response structures
      let servicesList = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          servicesList = response.data;
          console.log('Response is directly an array');
        } else if (response.data.services && Array.isArray(response.data.services)) {
          servicesList = response.data.services;
          console.log('Response has services property');
        } else if (response.data.data && Array.isArray(response.data.data)) {
          servicesList = response.data.data;
          console.log('Response has data property');
        }
      }
      
      console.log(`Loaded ${servicesList.length} services`);
      console.log('Services list:', servicesList);
      
      // Log each service ID and details
      servicesList.forEach((service, index) => {
        console.log(`Service ${index + 1}: ID=${service.id}, Bike ID=${service.bike_id}, Date=${service.service_date}, Company=${service.company_id}`);
      });
      
      setServices(servicesList);
      setError('');
      
      if (servicesList.length === 0) {
        console.warn('No services found.');
      } else {
        console.log(`✓ Successfully loaded ${servicesList.length} services into state`);
      }
    } catch (err) {
      console.error('=== API CALL ERROR ===');
      console.error('Error object:', err);
      console.error('Error response:', err.response);
      console.error('Error status:', err.response?.status);
      console.error('Error data:', err.response?.data);
      console.error('Error message:', err.message);
      console.error('Error config:', err.config);
      console.error('Request URL:', err.config?.url);
      console.error('Request method:', err.config?.method);
      
      const errorMsg = err.response?.data?.error || err.response?.data?.details || err.message || 'Failed to load services';
      const statusCode = err.response?.status;
      
      if (statusCode === 403) {
        setError(`Access denied (403): ${errorMsg}. Make sure you are logged in as super admin.`);
      } else if (statusCode === 401) {
        setError(`Authentication required (401): ${errorMsg}. Please log out and log back in.`);
      } else if (statusCode === 404) {
        setError(`Endpoint not found (404): The API endpoint may not exist. Check the browser console for more details.`);
      } else {
        setError(`Failed to load services (${statusCode || 'unknown'}): ${errorMsg}. Check the browser console (F12) for more details.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const filterServices = () => {
    let filtered = [...services];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(service => 
        (service.bike_vin && service.bike_vin.toLowerCase().includes(term)) ||
        (service.bike_brand && service.bike_brand.toLowerCase().includes(term)) ||
        (service.bike_model && service.bike_model.toLowerCase().includes(term)) ||
        (service.user_surname && service.user_surname.toLowerCase().includes(term)) ||
        (service.user_firstname && service.user_firstname.toLowerCase().includes(term)) ||
        (service.description && service.description.toLowerCase().includes(term))
      );
    }
    
    setFilteredServices(filtered);
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service? This action cannot be undone.')) {
      return;
    }
    
    try {
      await superAdminAPI.deleteService(serviceId);
      setSuccess('Service deleted successfully!');
      fetchServices();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete service');
    }
  };

  const getCompanyName = (companyId) => {
    if (!companyId) return 'N/A';
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : `Company ${companyId}`;
  };

  if (loading) {
    return <div className="loading">Loading services...</div>;
  }

  return (
    <div className="admin-section">
      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="section-header">
        <h2>All Services</h2>
        <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '10px'}}>
          <select
            value={selectedCompanyId || ''}
            onChange={(e) => setSelectedCompanyId(e.target.value ? parseInt(e.target.value) : null)}
            style={{padding: '5px'}}
          >
            <option value="">All Companies</option>
            {companies.map(company => (
              <option key={company.id} value={company.id}>{company.name}</option>
            ))}
          </select>
          
          <input
            type="date"
            placeholder="From Date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{padding: '5px'}}
          />
          
          <input
            type="date"
            placeholder="To Date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{padding: '5px'}}
          />
          
          <input
            type="text"
            placeholder="Search services..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{padding: '5px', minWidth: '200px'}}
          />
        </div>
      </div>

      <div className="table-responsive">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Date</th>
              <th>Bike</th>
              <th>Owner</th>
              <th>Company</th>
              <th>Description</th>
              <th>Cost</th>
              <th>Items</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredServices.length === 0 ? (
              <tr>
                <td colSpan="9" style={{textAlign: 'center'}}>No services found</td>
              </tr>
            ) : (
              filteredServices.map(service => (
                <React.Fragment key={service.id}>
                  <tr style={{ cursor: 'pointer' }} onClick={() => toggleRowExpand(service.id)}>
                    <td>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Icon 
                          name={expandedRows.has(service.id) ? 'chevronDown' : 'chevronRight'} 
                          size={14} 
                          color="muted" 
                          darkMode={darkMode} 
                        />
                        {service.id}
                      </span>
                    </td>
                    <td>{service.service_date ? new Date(service.service_date).toLocaleDateString() : 'N/A'}</td>
                    <td>
                      {service.bike_brand && service.bike_model 
                        ? `${service.bike_brand} ${service.bike_model} (${service.bike_vin})`
                        : service.bike_vin || 'N/A'}
                    </td>
                    <td>
                      {service.user_firstname && service.user_surname
                        ? `${service.user_firstname} ${service.user_surname}`
                        : service.user_email || 'N/A'}
                    </td>
                    <td>{getCompanyName(service.company_id)}</td>
                    <td style={{maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                      {service.description || 'N/A'}
                    </td>
                    <td>£{parseFloat(service.total_cost || 0).toFixed(2)}</td>
                    <td>{service.items ? service.items.length : 0}</td>
                    <td>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(service.id); }}
                        className="btn btn-sm btn-danger"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                  {expandedRows.has(service.id) && (
                    <tr>
                      <td colSpan="9" style={{ 
                        padding: '16px', 
                        background: darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                        borderBottom: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)'
                      }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                          {/* Service Details */}
                          <div>
                            <h4 style={{ margin: '0 0 12px 0', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '14px' }}>Service Details</h4>
                            <div style={{ fontSize: '13px', lineHeight: '1.6', color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
                              <p><strong>Description:</strong> {service.description || 'N/A'}</p>
                              <p><strong>Notes:</strong> {service.notes || 'N/A'}</p>
                              <p><strong>Status:</strong> {service.status || 'N/A'}</p>
                              <p><strong>Technician:</strong> {service.technician_name || 'N/A'}</p>
                              <p><strong>Total Cost:</strong> £{parseFloat(service.total_cost || 0).toFixed(2)}</p>
                            </div>
                          </div>
                          
                          {/* Service Items */}
                          {service.items && service.items.length > 0 && (
                            <div>
                              <h4 style={{ margin: '0 0 12px 0', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '14px' }}>Service Items ({service.items.length})</h4>
                              <div style={{ fontSize: '13px' }}>
                                {service.items.map((item, idx) => (
                                  <div key={idx} style={{ 
                                    padding: '8px', 
                                    marginBottom: '6px', 
                                    background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)', 
                                    borderRadius: '6px' 
                                  }}>
                                    <div style={{ color: darkMode ? '#fff' : '#1a1a2e', fontWeight: '500' }}>{item.name || item.description || 'Item'}</div>
                                    <div style={{ color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', fontSize: '12px' }}>
                                      Qty: {item.quantity || 1} | £{parseFloat(item.price || 0).toFixed(2)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Attachments */}
                          <div>
                            <h4 style={{ margin: '0 0 12px 0', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '14px' }}>Attachments</h4>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                              {service.receipt_image_url ? (
                                <a 
                                  href={service.receipt_image_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '6px',
                                    padding: '8px 12px',
                                    background: 'rgba(59, 130, 246, 0.1)',
                                    color: '#60a5fa',
                                    borderRadius: '6px',
                                    textDecoration: 'none',
                                    fontSize: '13px'
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Icon name="file" size={14} color="primary" darkMode={darkMode} />
                                  Receipt Image
                                </a>
                              ) : (
                                <span style={{ fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }}>No attachments</span>
                              )}
                              {service.attachments && service.attachments.map((att, idx) => (
                                <a 
                                  key={idx}
                                  href={att.url || att.path} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '6px',
                                    padding: '8px 12px',
                                    background: 'rgba(59, 130, 246, 0.1)',
                                    color: '#60a5fa',
                                    borderRadius: '6px',
                                    textDecoration: 'none',
                                    fontSize: '13px'
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Icon name="file" size={14} color="primary" darkMode={darkMode} />
                                  {att.name || `Attachment ${idx + 1}`}
                                </a>
                              ))}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default SuperAdminServices;






